import TradevuLogo from 'component/TradevuLogo';
import { XIcon } from 'lucide-react';
import React from 'react'
import { Modal } from 'react-bootstrap'
import dayjs from 'dayjs';
import { displayCurrencyAndAmount } from 'constants/FinanceConstants';
import capitalizeWords from 'utilities/capitalizeWords';
import getInitials from 'utilities/getInitials';
import Avatar from 'component/avatar';

const PoAndInvoicePreview = ({
  onClose,
  show,
  purchaseOrder
}) => {
  const gridStyle =
    "grid grid-cols-[1fr,55px,minmax(100px,120px),minmax(100px,115px)]"
  const splitTerm = '0';
  const { orderItems, currency, paymentTerms, totalAmount: subtotal, surCharge, note, inbound, outbound, deliveryDate, taxAmount } = purchaseOrder ?? {}
  const user = {
    bizName: outbound
      ?.businessName,
    bizImg: "",
    fullName: outbound?.email,
  }
  const dueDate =
    deliveryDate
      ? dayjs(
        deliveryDate
      ).format("MMM D, YYYY")
      : "No due date"

  const supplier = {
    name: inbound?.businessName,
    personalName: inbound?.email,
  }

  return (
    <Modal
      show={show}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      className="border-0 z-[9999]"
      onHide={onClose}
    >
      <Modal.Body
        className="bg-white rounded-md w-full  w-[90%] max-w-3xl h-[95vh]"
      >
        <div className="flex justify-between items-center font-[430] text-base !py-[9px] px-[10px] border-b-4 border-[#d9cdff] relative">
          <TradevuLogo />

          <div className="flex items-center gap-3 justify-end">
            PURCHASE ORDER
            {onClose && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onClose?.()
                }}
                className="text-sm text-gray-500 hover:text-gray-800"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "30px",
                  fontWeight: "bold",
                }}
              >
                <XIcon />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-start justify-between gap-4 px-[30px] my-[30px]">
          <div className="flex">
            <Avatar
              className={`font-[430] size-[42px] ${getInitials(user?.bizName ?? "t v").length < 4
                ? "text-[17px]"
                : "text-sm"
                }`}
              fullName={user?.bizName ?? "t v"}
              img={user?.bizImg ?? ""}
            />
            <p className="text-sm font-medium">
              {capitalizeWords(user?.bizName ?? "")}
              <span className="block font-normal text-[13px] text-main-grey !mt-1">
                {capitalizeWords(user?.fullName ?? "")}
              </span>
            </p>
          </div>

          {supplier && (
            <p className="text-sm flex flex-wrap items-center justify-start font-[470]">
              <span className="pr-1">To: {" "} </span>
              <span className="block white-space-nowrap">
                {capitalizeWords(supplier.name)}
              </span>
              <span className="block font-normal text-main-grey">
                {capitalizeWords(supplier.personalName)}
              </span>
            </p>
          )}
        </div>

        <p className="px-[30px] text-main text-sm !mt-[26px] !mb-[42px]">
          Due: {dueDate}
        </p>

        {/* Items Section */}
        <div>
          <div
            className={`${gridStyle} mx-[30px] !pb-[13px] !mb-[9px] text-sm font-normal text-main-grey border-b *:!bg-transparent`}
          >
            <span>Items</span>
            <span className="translate-x-3">Quantity</span>
            <span className="text-right">Unit Price</span>
            <span className="text-right">Total</span>
          </div>

          {orderItems?.map(({ id, itemName, pricePerUnit, quantity }) => (
            <p
              className={`${gridStyle} px-[30px] text-main font-normal text-[15px] !py-2`}
              key={id}
            >
              <span>{itemName}</span>
              <span className="translate-x-6">{quantity}</span>
              <span className="text-right">
                {displayCurrencyAndAmount(currency, pricePerUnit)}
              </span>
              <span className="text-right">
                {displayCurrencyAndAmount(currency, pricePerUnit * quantity)}
              </span>
            </p>
          ))}

          <div className="grid grid-cols-[47.5%,52.5%] mx-[30px] border-t !mt-1.5">
            <span />
            <p className="flex items-center justify-end font-normal !py-[18px] text-[15px] text-main">
              <span className="mr-auto text-sm text-main-grey">Subtotal</span>
              {displayCurrencyAndAmount(currency, Number(subtotal))}
            </p>
          </div>

          <div className="grid grid-cols-[47.5%,52.5%] mx-[30px] border-t">
            <span />
            <p className="flex items-center justify-end font-normal !py-[18px] text-[15px] text-main">
              <span className="mr-auto text-sm text-main-grey">Surcharge</span>
              {displayCurrencyAndAmount(currency, Number(surCharge))}
            </p>
          </div>

          <div className="grid grid-cols-[47.5%,52.5%] mx-[30px] border-t">
            <span />
            <p className="flex items-center justify-end font-normal !py-[18px] text-[15px] text-main">
              <span className="mr-auto text-sm text-main-grey">Tax (7.5%)</span>
              {displayCurrencyAndAmount(currency, Number(taxAmount))}
            </p>
          </div>

          <div className="grid grid-cols-[47.5%,52.5%] mx-[30px] border-t">
            <span />
            <p className="flex items-center justify-end !py-[18px] text-[15px] font-semibold">
              <span className="mr-auto text-sm font-normal text-main-grey">
                Amount due
              </span>
              {displayCurrencyAndAmount(currency, Number(taxAmount))}
            </p>
          </div>
        </div>

        {/* Payment & Notes */}
        <div className="rounded-lg py-3.5 px-4">
          <p className="text-[13px] text-main-grey font-normal">
            Payment terms
          </p>
          <p className="text-sm font-medium !mt-1">{paymentTerms}</p>
          {paymentTerms?.startsWith("Split") && (
            <p className="font-normal text-[13px] text-main !mt-1.5">
              {splitTerm}
            </p>
          )}
          {note && (
            <>
              <p className="text-[13px] text-main-grey font-normal !mt-5">
                Notes
              </p>
              <p className="text-sm font-medium !mt-1">{note}</p>
            </>
          )}
        </div>

        <p className="text-[#ab6bff] px-4 font-[375] mt-8 mb-1.5 text-left">
          Thank you!
        </p>
        <p className="text-xs leading-[17px] text-main-grey font-[375] px-4 !pb-4 !text-left">
          If you have questions for us, please call a representative on{" "}
          <a
            href="tel:+2347073183321"
            className="!text-[#6F00FF] hover:underline"
          >
            +2347073183321
          </a>{" "}
          or{" "}
          <a
            href="tel:+447903175084"
            className="!text-[#6F00FF] hover:underline"
          >
            +447903175084
          </a>
          . You can also send us an email at{" "}
          <a
            href="mailto:hello@tradevu.co"
            className="!text-[#6F00FF] hover:underline"
          >
            hello@tradevu.co
          </a>
          . Thank you for using Tradevu.
        </p>
      </Modal.Body>
    </Modal>
  )
}

export default PoAndInvoicePreview